/**
 * Size-capped LRU eviction engine for `createRecordStore.ts` — extracted to
 * keep the primary store file under the project's line-count guidance.
 *
 * This module owns no state of its own beyond the LRU bookkeeping
 * (`lruOrder`, `maxEntries`, eviction listeners). It mutates the record
 * store's `records`/`recordSnapshots`/`snapshot` state through the injected
 * `EvictionEngineDeps` accessors, mirroring the copy-on-write pattern used by
 * `commit`/`remove` in `createRecordStore.ts`, and fires the store's own
 * change-notification functions so callers see the same emission semantics
 * whether the write came from a commit or an eviction pass.
 */

import type { StoredRecord } from './recordStoreTypes';

/** A record evicted by `enforceSizeCap`, passed to eviction listeners. */
export interface EvictedRecord<T> {
  id: string;
  data: T;
}

export interface EvictionEngineDeps<T> {
  getRecords: () => Map<string, StoredRecord<T>>;
  setRecords: (records: Map<string, StoredRecord<T>>) => void;
  getRecordSnapshots: () => Map<string, T | undefined>;
  setRecordSnapshots: (snapshots: Map<string, T | undefined>) => void;
  setSnapshot: (snapshot: ReadonlyMap<string, StoredRecord<T>>) => void;
  getBatchDepth: () => number;
  getRecordListeners: () => Map<string, Set<() => void>>;
  emitRecordChange: (id: string) => void;
  emitChange: () => void;
  isProtected?: (id: string) => boolean;
  maxEntries?: number;
}

export interface EvictionEngine<T> {
  touch(id: string): void;
  forget(id: string): void;
  setMaxEntries(n: number): void;
  subscribeToEviction(cb: (evicted: EvictedRecord<T>[]) => void): () => void;
  isEvictable(id: string): boolean;
  evictRecord(id: string): EvictedRecord<T> | null;
  enforceSizeCap(): void;
  afterBatchCommit(): void;
  reset(): void;
}

export function createEvictionEngine<T>(
  deps: EvictionEngineDeps<T>
): EvictionEngine<T> {
  /** Insertion-ordered LRU list — front (iteration order) = LRU, back = MRU. */
  let lruOrder = new Map<string, true>();
  /** Store size above which eviction kicks in. */
  let maxEntries = deps.maxEntries ?? Infinity;
  /** Listeners notified (via `queueMicrotask`) with the records evicted by a pass. */
  let evictionListeners = new Set<(evicted: EvictedRecord<T>[]) => void>();
  /** Re-entrancy guard — prevents `enforceSizeCap` from recursing via its own writes. */
  let isEnforcing = false;
  /** Throttle for the "all over-cap entries are protected" warning. */
  let floorWarnEmitted = false;

  /** Record a committed write or subscribe against `id`, moving it to the MRU end. */
  function touch(id: string): void {
    // delete-then-set moves an existing key to the end — `Map.set` alone does
    // NOT reorder an already-present key.
    lruOrder.delete(id);
    lruOrder.set(id, true);
  }

  /** Drop `id` from LRU tracking (e.g. on removal/eviction). */
  function forget(id: string): void {
    lruOrder.delete(id);
  }

  function setMaxEntries(n: number): void {
    maxEntries = n;
  }

  function subscribeToEviction(
    cb: (evicted: EvictedRecord<T>[]) => void
  ): () => void {
    evictionListeners.add(cb);
    return () => {
      evictionListeners.delete(cb);
    };
  }

  /**
   * Whether `id` is safe to evict: not subscribed (an active record listener)
   * and not protected by the injected `isProtected` predicate.
   */
  function isEvictable(id: string): boolean {
    if ((deps.getRecordListeners().get(id)?.size ?? 0) > 0) {
      return false;
    }
    if (deps.isProtected?.(id) === true) {
      return false;
    }
    return true;
  }

  /**
   * Evict a single record: removes it from `records`/`recordSnapshots` and
   * drops it from LRU tracking. Returns the evicted `{ id, data }` pair for
   * eviction-listener consumers, or `null` if the id was already gone.
   */
  function evictRecord(id: string): EvictedRecord<T> | null {
    const records = deps.getRecords();
    const existing = records.get(id);
    if (!existing) {
      return null;
    }
    records.delete(id);
    deps.getRecordSnapshots().delete(id);
    forget(id);
    return { id, data: existing.data };
  }

  /**
   * Evict least-recently-touched entries once the store exceeds
   * `maxEntries`. No-op while a batch is in progress or during a nested
   * enforcement pass.
   */
  function enforceSizeCap(): void {
    if (deps.getBatchDepth() > 0 || isEnforcing) {
      return;
    }
    const records = deps.getRecords();
    if (records.size <= maxEntries) {
      return;
    }
    isEnforcing = true;
    try {
      for (const id of records.keys()) {
        if (!lruOrder.has(id)) {
          lruOrder.set(id, true); // safety net: untouched records are oldest
        }
      }
      const victimIds: string[] = [];
      let projectedSize = records.size;
      for (const id of lruOrder.keys()) {
        if (projectedSize <= maxEntries) {
          break;
        }
        if (!records.has(id)) {
          continue;
        }
        if (!isEvictable(id)) {
          continue;
        }
        victimIds.push(id);
        projectedSize--;
      }
      if (victimIds.length === 0) {
        if (!floorWarnEmitted) {
          floorWarnEmitted = true;
          console.warn(
            `[createRecordStore] size cap ${maxEntries} exceeded (current ${records.size}) but all ` +
              'over-cap records are protected — cannot evict.'
          );
        }
        return;
      }
      floorWarnEmitted = false;
      deps.setRecords(new Map(records));
      deps.setRecordSnapshots(new Map(deps.getRecordSnapshots()));
      const evicted: EvictedRecord<T>[] = [];
      for (const id of victimIds) {
        const rec = evictRecord(id);
        if (rec) {
          evicted.push(rec);
        }
      }
      deps.setSnapshot(deps.getRecords());
      for (const rec of evicted) {
        deps.emitRecordChange(rec.id);
      }
      deps.emitChange();
      const batch = [...evicted];
      queueMicrotask(() => {
        for (const cb of evictionListeners) {
          cb(batch);
        }
      });
    } finally {
      isEnforcing = false;
    }
  }

  /** Hook point invoked after a commit or batch promotion to enforce the size cap. */
  function afterBatchCommit(): void {
    enforceSizeCap();
  }

  function reset(): void {
    lruOrder = new Map();
    maxEntries = deps.maxEntries ?? Infinity;
    evictionListeners = new Set();
    isEnforcing = false;
    floorWarnEmitted = false;
  }

  return {
    touch,
    forget,
    setMaxEntries,
    subscribeToEviction,
    isEvictable,
    evictRecord,
    enforceSizeCap,
    afterBatchCommit,
    reset,
  };
}
